import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldCheck,
  ArrowLeft,
  Calendar,
  Lock,
  FileText,
  UserCheck,
  Building,
  CheckCircle2,
  Globe,
  Mail,
  Scale,
  Eye,
  Database,
  KeyRound
} from 'lucide-react';
import { Link } from 'react-router-dom';

const BlebePrivacyPolicy = ({ isDark, lang: initialLang = 'es' }) => {
  const [docLang, setDocLang] = useState(initialLang);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isEs = docLang === 'es';

  return (
    <div className={`min-h-screen pt-28 sm:pt-32 pb-24 px-4 sm:px-6 transition-colors duration-500 overflow-hidden ${
      isDark ? 'bg-[#0A0F1A] text-white' : 'bg-[#F8FAFC] text-slate-900'
    }`}>
      {/* Luz ambiental decorativa */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className={`absolute top-20 left-1/4 w-72 sm:w-96 h-72 sm:h-96 rounded-full blur-[140px] opacity-20 ${
          isDark ? 'bg-indigo-600' : 'bg-indigo-300'
        }`} />
        <div className={`absolute bottom-20 right-1/4 w-72 sm:w-96 h-72 sm:h-96 rounded-full blur-[140px] opacity-15 ${
          isDark ? 'bg-blue-600' : 'bg-blue-300'
        }`} />
      </div>

      <div className="container mx-auto max-w-4xl relative z-10 text-left">
        
        {/* BARRA SUPERIOR: REGRESAR + SELECTOR DE IDIOMA DEL DOCUMENTO */}
        <div className="flex items-center justify-between gap-4 mb-8 sm:mb-12">
          <Link
            to="/proyectos/Blebe"
            className={`inline-flex items-center gap-2 font-black uppercase text-[10px] tracking-widest transition-all hover:-translate-x-1.5 ${
              isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-indigo-600'
            }`}
          >
            <ArrowLeft size={16} /> {isEs ? 'Volver a Bleblé' : 'Back to Bleblé'}
          </Link>

          {/* Selector de idioma exclusivo para el documento legal */}
          <div className={`p-1 rounded-xl border flex items-center gap-1 ${
            isDark ? 'bg-slate-900/80 border-white/10' : 'bg-slate-200/80 border-slate-300'
          }`}>
            <Globe size={14} className="ml-2 text-indigo-500" />
            <button
              onClick={() => setDocLang('es')}
              className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
                docLang === 'es'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : (isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-950')
              }`}
            >
              ES
            </button>
            <button
              onClick={() => setDocLang('en')}
              className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
                docLang === 'en'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : (isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-950')
              }`}
            >
              EN
            </button>
          </div>
        </div>

        {/* ENCABEZADO DEL DOCUMENTO */}
        <div className="mb-10 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-indigo-500/20 bg-indigo-500/10 mb-4">
            <ShieldCheck size={14} className="text-indigo-400" />
            <span className="text-[10px] font-black uppercase tracking-[0.25em] text-indigo-400">
              {isEs ? 'MARCO NORMATIVO LFPDPPP' : 'LFPDPPP REGULATORY COMPLIANCE'}
            </span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-black tracking-tight mb-4 leading-tight">
            {isEs ? 'Aviso de Privacidad' : 'Privacy Notice'}
          </h1>
          <p className="text-xl sm:text-2xl font-black italic text-indigo-500 mb-4">
            Bleblé
          </p>

          <div className="flex items-center gap-2 text-xs font-bold opacity-60">
            <Calendar size={14} />
            <span>{isEs ? 'Última actualización: 20 de junio del 2026' : 'Last updated: June 20, 2026'}</span>
          </div>
        </div>

        {/* CONTENEDOR TIPO PLIEGO LEGAL / GLASS CARD */}
        <div className={`p-6 sm:p-12 rounded-[2.5rem] sm:rounded-[3rem] border backdrop-blur-3xl transition-all ${
          isDark 
            ? 'bg-slate-900/60 border-white/10 shadow-2xl shadow-black/40' 
            : 'bg-white border-slate-200 shadow-xl shadow-indigo-500/5'
        }`}>
          
          <AnimatePresence mode="wait">
            {isEs ? (
              <motion.div
                key="es"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className="space-y-10 text-sm sm:text-base font-medium leading-relaxed opacity-85"
              >
                {/* Introducción */}
                <p className="border-l-2 border-indigo-500 pl-4 py-1 italic opacity-95">
                  Bleblé, con domicilio en Ciudad de México, Ciudad de México, es responsable del tratamiento de los datos personales recabados a través de su plataforma tecnológica, sitio web, aplicaciones, formularios digitales y procesos relacionados con servicios de reclutamiento, validación documental, estudios socioeconómicos y evaluación de candidatos.
                </p>

                <p>
                  El presente Aviso de Privacidad se emite en cumplimiento de la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP), su Reglamento y demás disposiciones aplicables en los Estados Unidos Mexicanos.
                </p>

                {/* Sección I */}
                <section className="space-y-4">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    I. Datos Personales Recabados
                  </h2>
                  <p>Bleblé podrá recabar, almacenar, utilizar, transferir y tratar los siguientes datos personales:</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                    <div className={`p-4 rounded-2xl border ${isDark ? 'bg-slate-800/40 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                      <h3 className="font-black text-xs uppercase tracking-wider text-indigo-400 mb-2">A. Identificación</h3>
                      <ul className="space-y-1 text-xs opacity-80 list-disc list-inside">
                        <li>Nombre completo</li>
                        <li>Fecha de nacimiento</li>
                        <li>CURP y RFC</li>
                        <li>Nacionalidad</li>
                        <li>Fotografía y firma</li>
                        <li>Identificaciones oficiales</li>
                      </ul>
                    </div>

                    <div className={`p-4 rounded-2xl border ${isDark ? 'bg-slate-800/40 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                      <h3 className="font-black text-xs uppercase tracking-wider text-indigo-400 mb-2">B. Contacto</h3>
                      <ul className="space-y-1 text-xs opacity-80 list-disc list-inside">
                        <li>Domicilio particular</li>
                        <li>Correo electrónico</li>
                        <li>Número telefónico móvil y fijo</li>
                      </ul>
                    </div>

                    <div className={`p-4 rounded-2xl border ${isDark ? 'bg-slate-800/40 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                      <h3 className="font-black text-xs uppercase tracking-wider text-indigo-400 mb-2">C. Laborales y Académicos</h3>
                      <ul className="space-y-1 text-xs opacity-80 list-disc list-inside">
                        <li>Historial y referencias laborales</li>
                        <li>Escolaridad y certificaciones</li>
                        <li>Experiencia profesional</li>
                      </ul>
                    </div>

                    <div className={`p-4 rounded-2xl border ${isDark ? 'bg-slate-800/40 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                      <h3 className="font-black text-xs uppercase tracking-wider text-indigo-400 mb-2">D. Patrimoniales y Socioeconómicos</h3>
                      <ul className="space-y-1 text-xs opacity-80 list-disc list-inside">
                        <li>Ingresos y situación patrimonial</li>
                        <li>Información familiar y vivienda</li>
                        <li>Estudios socioeconómicos</li>
                      </ul>
                    </div>
                  </div>

                  <div className={`p-4 rounded-2xl border mt-3 ${isDark ? 'bg-slate-800/40 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                    <h3 className="font-black text-xs uppercase tracking-wider text-indigo-400 mb-2">E. Datos Tecnológicos</h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs opacity-80 list-disc list-inside">
                      <li>Dirección IP e información del dispositivo</li>
                      <li>Geolocalización temporal y registros de acceso</li>
                      <li>Cookies y tecnologías similares</li>
                    </ul>
                  </div>
                </section>

                {/* Sección II */}
                <section className="space-y-3">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    II. Datos Personales Sensibles
                  </h2>
                  <p>
                    Bleblé podrá tratar datos personales sensibles cuando resulten necesarios para la prestación de los servicios contratados, incluyendo información socioeconómica, validaciones de antecedentes y documentación oficial.
                  </p>
                  <p>
                    El tratamiento de dichos datos se realizará bajo estrictas medidas de seguridad administrativas, técnicas y físicas, garantizando su confidencialidad y protección.
                  </p>
                </section>

                {/* Sección III */}
                <section className="space-y-3">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    III. Finalidades del Tratamiento de Datos
                  </h2>
                  <p className="font-bold text-xs uppercase tracking-wider text-indigo-300">Finalidades Principales:</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs opacity-80">
                    <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-indigo-500 shrink-0" /> Gestión de reclutamiento y selección</li>
                    <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-indigo-500 shrink-0" /> Elaboración de estudios socioeconómicos</li>
                    <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-indigo-500 shrink-0" /> Verificación documental e identidad</li>
                    <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-indigo-500 shrink-0" /> Validación de referencias laborales</li>
                    <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-indigo-500 shrink-0" /> Reportes y análisis de riesgo</li>
                    <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-indigo-500 shrink-0" /> Administración de cuentas de usuario</li>
                    <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-indigo-500 shrink-0" /> Prevención de fraude y delitos</li>
                    <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-indigo-500 shrink-0" /> Facturación y procesos administrativos</li>
                  </ul>

                  <p className="font-bold text-xs uppercase tracking-wider text-indigo-300 pt-3">Finalidades Secundarias:</p>
                  <p className="text-xs opacity-75">
                    Estadística interna, mejora continua de servicios, desarrollo tecnológico, evaluación de experiencia de usuario y comunicaciones informativas o comerciales.
                  </p>
                </section>

                {/* Sección IV */}
                <section className="space-y-3">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    IV. Transferencia de Datos Personales
                  </h2>
                  <p>Bleblé podrá transferir datos personales exclusivamente a:</p>
                  <ul className="space-y-1 text-xs opacity-80 list-disc list-inside">
                    <li>Empresas clientes involucradas en procesos de contratación.</li>
                    <li>Proveedores tecnológicos y de almacenamiento cloud.</li>
                    <li>Autoridades competentes cuando exista mandato legal.</li>
                    <li>Terceros encargados de verificaciones y validaciones autorizadas.</li>
                  </ul>
                  <p className="font-bold text-xs pt-1 text-indigo-400">
                    Bleblé no venderá ni comercializará información personal de los usuarios.
                  </p>
                </section>

                {/* Sección V */}
                <section className="space-y-3">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    V. Medidas de Seguridad
                  </h2>
                  <p>
                    Implementamos medidas de seguridad administrativas, técnicas y físicas que incluyen cifrado de información, accesos restringidos por roles, autenticación segura, monitoreo de actividad, control de permisos y auditorías internas continuas. El usuario reconoce que ningún sistema tecnológico es invulnerable.
                  </p>
                </section>

                {/* Sección VI */}
                <section className="space-y-3">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    VI. Conservación de la Información
                  </h2>
                  <div className={`p-4 rounded-2xl border ${isDark ? 'bg-indigo-950/20 border-indigo-500/20' : 'bg-indigo-50/60 border-indigo-100'}`}>
                    <p className="text-xs sm:text-sm leading-relaxed">
                      Los datos personales y la documentación proporcionada por el candidato serán conservados durante un <strong>periodo máximo de seis (6) meses</strong> contados a partir de la fecha de registro o de la última actualización de su expediente. Durante dicho periodo, y únicamente con el consentimiento expreso del candidato, su información podrá ser compartida con empresas clientes con vacantes compatibles. Cumplido dicho plazo, la información será eliminada, bloqueada o anonimizada.
                    </p>
                  </div>
                </section>

                {/* Sección VII */}
                <section className="space-y-3">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    VII. Derechos ARCO
                  </h2>
                  <p>
                    El titular de los datos personales podrá ejercer en cualquier momento sus derechos de <strong>Acceso, Rectificación, Cancelación y Oposición (ARCO)</strong>, así como revocar su consentimiento.
                  </p>
                  <p className="text-xs opacity-75">
                    Las solicitudes relacionadas deberán dirigirse a los canales oficiales de privacidad de Bleblé señalados al final de este documento.
                  </p>
                </section>

                {/* Sección VIII, IX, X, XI */}
                <section className="space-y-3">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    VIII. Cookies y Tecnologías Similares
                  </h2>
                  <p className="text-xs opacity-80">
                    Se emplean cookies y web beacons para optimizar la experiencia, monitorear patrones de tráfico y robustecer la seguridad. Puede configurarse el navegador para deshabilitarlas.
                  </p>
                </section>

                <section className="space-y-3">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    IX. Consentimiento
                  </h2>
                  <p className="text-xs opacity-80">
                    Al acceder, registrarse o cargar documentación, el usuario otorga su consentimiento para el tratamiento de su información según lo estipulado en este instrumento.
                  </p>
                </section>

                <section className="space-y-3">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    X. Modificaciones al Aviso
                  </h2>
                  <p className="text-xs opacity-80">
                    Bleblé se reserva el derecho de actualizar este Aviso de Privacidad en cualquier momento ante reformas legales o ajustes de infraestructura tecnológica.
                  </p>
                </section>

                <section className="space-y-3 border-t border-current/10 pt-6">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    XI. Contacto y Consultas
                  </h2>
                  <p className="text-xs opacity-80">
                    Para el ejercicio de derechos ARCO o cualquier aclaración sobre el tratamiento de su información, comuníquese con el equipo de Privacidad y Datos a través de los medios oficiales de Bleblé y la firma TDSMX.
                  </p>
                </section>
              </motion.div>
            ) : (
              <motion.div
                key="en"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className="space-y-10 text-sm sm:text-base font-medium leading-relaxed opacity-85"
              >
                {/* Introduction */}
                <p className="border-l-2 border-indigo-500 pl-4 py-1 italic opacity-95">
                  Bleblé, residing in Mexico City, Mexico City, is responsible for the processing of personal data collected through its technological platform, website, applications, digital forms, and processes related to recruitment services, document validation, background checks (socioeconomic studies), and candidate evaluations.
                </p>

                <p>
                  This Privacy Notice is issued in compliance with the Federal Law on Protection of Personal Data Held by Private Parties (Ley Federal de Protección de Datos Personales en Posesión de los Particulares — LFPDPPP), its Regulations, and other applicable provisions in the United Mexican States.
                </p>

                {/* Section I */}
                <section className="space-y-4">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    I. Personal Data Collected
                  </h2>
                  <p>Bleblé may collect, store, use, transfer, and process the following personal data:</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                    <div className={`p-4 rounded-2xl border ${isDark ? 'bg-slate-800/40 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                      <h3 className="font-black text-xs uppercase tracking-wider text-indigo-400 mb-2">A. Identification Data</h3>
                      <ul className="space-y-1 text-xs opacity-80 list-disc list-inside">
                        <li>Full name</li>
                        <li>Date of birth</li>
                        <li>CURP & RFC numbers</li>
                        <li>Nationality</li>
                        <li>Photograph & signature</li>
                        <li>Official government IDs</li>
                      </ul>
                    </div>

                    <div className={`p-4 rounded-2xl border ${isDark ? 'bg-slate-800/40 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                      <h3 className="font-black text-xs uppercase tracking-wider text-indigo-400 mb-2">B. Contact Data</h3>
                      <ul className="space-y-1 text-xs opacity-80 list-disc list-inside">
                        <li>Home address</li>
                        <li>Email address</li>
                        <li>Telephone and mobile numbers</li>
                      </ul>
                    </div>

                    <div className={`p-4 rounded-2xl border ${isDark ? 'bg-slate-800/40 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                      <h3 className="font-black text-xs uppercase tracking-wider text-indigo-400 mb-2">C. Employment & Academic Data</h3>
                      <ul className="space-y-1 text-xs opacity-80 list-disc list-inside">
                        <li>Employment history & references</li>
                        <li>Educational background</li>
                        <li>Certifications & career experience</li>
                      </ul>
                    </div>

                    <div className={`p-4 rounded-2xl border ${isDark ? 'bg-slate-800/40 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                      <h3 className="font-black text-xs uppercase tracking-wider text-indigo-400 mb-2">D. Financial & Socioeconomic Data</h3>
                      <ul className="space-y-1 text-xs opacity-80 list-disc list-inside">
                        <li>Income & asset status</li>
                        <li>Family and housing details</li>
                        <li>Background checks & studies</li>
                      </ul>
                    </div>
                  </div>

                  <div className={`p-4 rounded-2xl border mt-3 ${isDark ? 'bg-slate-800/40 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                    <h3 className="font-black text-xs uppercase tracking-wider text-indigo-400 mb-2">E. Technological Data</h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs opacity-80 list-disc list-inside">
                      <li>IP address & hardware device information</li>
                      <li>Temporary geolocation & system access logs</li>
                      <li>Cookies and related tracking tools</li>
                    </ul>
                  </div>
                </section>

                {/* Section II */}
                <section className="space-y-3">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    II. Sensitive Personal Data
                  </h2>
                  <p>
                    Bleblé may process sensitive personal data when strictly required for providing candidate evaluation services, background verification, and formal documentation review.
                  </p>
                  <p>
                    All sensitive records are handled under strict physical, administrative, and technological security controls guaranteeing absolute confidentiality.
                  </p>
                </section>

                {/* Section III */}
                <section className="space-y-3">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    III. Purposes of Data Processing
                  </h2>
                  <p className="font-bold text-xs uppercase tracking-wider text-indigo-300">Primary Purposes:</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs opacity-80">
                    <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-indigo-500 shrink-0" /> Recruitment and vetting administration</li>
                    <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-indigo-500 shrink-0" /> Execution of socioeconomic studies</li>
                    <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-indigo-500 shrink-0" /> Document verification and identity check</li>
                    <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-indigo-500 shrink-0" /> Employment and reference checks</li>
                    <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-indigo-500 shrink-0" /> Risk assessment and candidate reports</li>
                    <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-indigo-500 shrink-0" /> User account and profile management</li>
                    <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-indigo-500 shrink-0" /> Fraud prevention and legal safety</li>
                    <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-indigo-500 shrink-0" /> Billing and operational compliance</li>
                  </ul>

                  <p className="font-bold text-xs uppercase tracking-wider text-indigo-300 pt-3">Secondary Purposes:</p>
                  <p className="text-xs opacity-75">
                    Internal analytics, continuous platform optimization, algorithmic enhancements, user journey evaluation, and non-invasive service notices.
                  </p>
                </section>

                {/* Section IV */}
                <section className="space-y-3">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    IV. Transfer of Personal Data
                  </h2>
                  <p>Bleblé may disclose personal data strictly to:</p>
                  <ul className="space-y-1 text-xs opacity-80 list-disc list-inside">
                    <li>Client corporations running matching hiring pipelines.</li>
                    <li>Secure cloud storage and infrastructure vendors.</li>
                    <li>Competent regulatory authorities upon legal request.</li>
                    <li>Authorized third parties contracted for validation workflows.</li>
                  </ul>
                  <p className="font-bold text-xs pt-1 text-indigo-400">
                    Bleblé does not sell or commercially exploit candidates' personal data.
                  </p>
                </section>

                {/* Section V */}
                <section className="space-y-3">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    V. Security Measures
                  </h2>
                  <p>
                    We deploy end-to-end data encryption, role-based access restrictions, multi-factor authentication, event logging, and routine technical audits to prevent unauthorized leakage, alteration, or damage.
                  </p>
                </section>

                {/* Section VI */}
                <section className="space-y-3">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    VI. Data Retention
                  </h2>
                  <div className={`p-4 rounded-2xl border ${isDark ? 'bg-indigo-950/20 border-indigo-500/20' : 'bg-indigo-50/60 border-indigo-100'}`}>
                    <p className="text-xs sm:text-sm leading-relaxed">
                      Personal files and documents are preserved for a <strong>maximum period of six (6) months</strong> from the date of submission or last dossier update. During this timeframe, and strictly with candidate consent, data may be reviewed by hiring clients. Upon reaching the 6-month period, records will be purged, locked, or anonymized in compliance with the law.
                    </p>
                  </div>
                </section>

                {/* Section VII */}
                <section className="space-y-3">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    VII. ARCO Rights
                  </h2>
                  <p>
                    Data subjects retain full statutory rights of <strong>Access, Rectification, Cancellation, and Opposition (ARCO)</strong>, as well as the right to revoke consent at any time.
                  </p>
                  <p className="text-xs opacity-75">
                    Formal petitions must be submitted through the official data compliance contact channels outlined below.
                  </p>
                </section>

                {/* Sections VIII - XI */}
                <section className="space-y-3">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    VIII. Cookies & Tracking
                  </h2>
                  <p className="text-xs opacity-80">
                    Browser cookies and beacons assist in traffic diagnostics and account session protection. Settings can be reconfigured in your browser client.
                  </p>
                </section>

                <section className="space-y-3">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    IX. Consent
                  </h2>
                  <p className="text-xs opacity-80">
                    By accessing, uploading credentials, or registering on Bleblé, the user acknowledges full understanding and grants explicit consent to the handling of their records.
                  </p>
                </section>

                <section className="space-y-3">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    X. Policy Amendments
                  </h2>
                  <p className="text-xs opacity-80">
                    Bleblé reserves the right to modify this notice at any moment to conform with new statutory mandates or technical evolutions.
                  </p>
                </section>

                <section className="space-y-3 border-t border-current/10 pt-6">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-indigo-400 uppercase">
                    XI. Contact
                  </h2>
                  <p className="text-xs opacity-80">
                    For inquiries or the exercise of ARCO rights, please reach out through the official support and compliance channels of Bleblé and TDSMX.
                  </p>
                </section>
              </motion.div>
            )}
          </AnimatePresence>

        </div>

      </div>
    </div>
  );
};

export default BlebePrivacyPolicy;